import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EliteFormFlowChartDataService } from "@features/elite-form/services/elite-form-flow-chart-data.service";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { EliteFormManagerService } from "@features/elite-form/services/elite-form-manager.service";
import {filter, take, takeUntil} from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { EliteFormServiceFactory } from "@features/elite-form/services/elite-form-service-factory.service";
import {
  DialogField
} from "@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models";
import { EliteFormPageModel } from "@features/elite-form/models/elite-form-common-data-models";
import { EliteFormValidationService } from '@features/elite-form/services/form-validation.service';
import { getCurrentPlatformLink, ParticleState } from '@shared/store/reducers/particle.reduer';
import { Store } from '@ngrx/store';
import { MagicLinkModel } from '@shared/models/platform-tool-models';
import { FuseSplashScreenService } from "@barebone-fuse-services/splash-screen.service";
import { InvalidateSubject } from "@shared/helpers/invalidate.subject";
import { FormResponseViewService } from "@features/elite-form/services/form-response-view.service";
import { PlatformCommand } from '@shared/commands/command-base';
import { CommandService } from '@core/services/command.service';
import { Api } from '@shared/constants/common-field.constants';
import { EliteformTableOfContentsService } from '@shared/components/platform-tool/platform-tool-dialog-definition/services/eliteform-table-of-contents.service';

@Component({
    standalone: false,
    selector: 'app-elite-form-response-container',
  templateUrl: './elite-form-response-container.component.html',
  styleUrls: ['./elite-form-response-container.component.scss']
})
export class EliteFormResponseContainerComponent implements OnInit, OnDestroy {
  @Input() eliteFormId: string;
  @Input() itemId: string;
  @Output() pageChange = new EventEmitter<number>();
  pages = [];
  platformDialogsByPage: any;
  inputForm: FormGroup;
  platformDialogSectionsMapping = {};
  ngUnsubscribe$ = new Subject<void>();
  eliteFormManagerService: EliteFormManagerService;
  @Output() formValueChange = new EventEmitter();
  showPageOptions: boolean = false;

  totalErrors: number = 0;
  private currentPlatformLink: MagicLinkModel;
  isReadOnly = false;
  isDeleteButtonDisabled = false;
  private tocSubscription: Subscription;

  /**
   * FM-E-170-10: a field-level deep link is consumed once. Pages are re-fetched on a per-section
   * Include and after multi-response edits, and re-applying the link on each of those would yank the
   * user back to where they arrived from every time they changed something.
   */
  private isFieldDeepLinkApplied = false;

  /** The anchor the deep link is currently holding in view; supersedes any earlier hold. */
  private deepLinkAnchorId: string | null = null;

  /**
   * How long a repeating group must read as empty before it is believed — 100ms per tick, so 3s.
   *
   * A group renders nothing while its rows are still being fetched, so "empty" and "not loaded yet"
   * look identical. This is the margin over the fetch; too short and a slow load gets a duplicate row
   * written into a response that already had one.
   */
  private static readonly EmptyGroupConfirmTicks = 30;

  /** Groups already asked for a row, so the poll cannot request the same one repeatedly. */
  private readonly sectionsRequested = new Set<string>();

  /** Consecutive ticks each group has read as empty — see EmptyGroupConfirmTicks. */
  private readonly emptyObservations = new Map<string, number>();

  /** Panel headers already opened, so the poll cannot toggle one back shut. */
  private readonly panelsOpened = new WeakSet<HTMLElement>();

  constructor(private eliteFormDataService: EliteFormFlowChartDataService,
    private validationService: EliteFormValidationService,
    private eliteFormServiceFactory: EliteFormServiceFactory,
    private formResponseViewService: FormResponseViewService,
    private formBuilder: FormBuilder,
    private particleStore: Store<ParticleState>,
    private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private commandService: CommandService,
    private tocService: EliteformTableOfContentsService
  ) {
  }

  ngOnInit(): void {
    this.particleStore.select(getCurrentPlatformLink).pipe(takeUntil(this.ngUnsubscribe$)).subscribe((platformLink: any) => {
      this.currentPlatformLink = platformLink;
      this.isReadOnly = platformLink.fieldValues["IsReadOnly"] ?? false;
    });
    this.formResponseViewService.setupAutoSave(this.ngUnsubscribe$);
    this.eliteFormManagerService = this.eliteFormServiceFactory.getEliteFormManagerService(this.eliteFormId);
    this.inputForm = this.formBuilder.group({});

    this.getPlatformDialogsByFlowchartId();
    this.formResponseViewService.updateTableOfContents();
    /*this.eliteFormFlowChartDataService.invalidateResponsePagesSubject.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(o => {
      this.getPlatformDialogsByFlowchartId();
    });*/
    /*this.eliteFormFlowChartDataService.responseInvalidateSubject.pipe(takeUntil(this.ngUnsubscribe$), filter(eId => eId === this.eliteFormId)).subscribe(o => {
      this.getPlatformDialogsByFlowchartId();
    });*/

    this.validationService.getValidationState()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(state => {
        this.totalErrors = state.totalErrors;
      });

    // Subscribe to TOC page index changes
    this.tocSubscription = this.tocService.pageIndex$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(pageIndex => {
      if (typeof pageIndex === 'number') {
        if(this.formResponseViewService.currentPageIndex !== pageIndex){
          this.formResponseViewService.currentEliteFormPage = this.pages[pageIndex];
          if (pageIndex >= 0) {
            this.formResponseViewService.currentPageIndex = -1;
            setTimeout(() => {
              this.formResponseViewService.currentPageIndex = pageIndex;
              this.updateDeleteButtonState();
            }, 0);
          }
        }
      }
    });

    this.tocService.setCurrentPage(this.formResponseViewService.currentPageIndex);

    // Scroll-to-field orchestration. Lives here (always-rendered) rather than in the
    // per-group section component: a single subscription drives the whole form, and the
    // signal is never missed while a target section is still lazily mounting (the section
    // sits behind matExpansionPanelContent, so it does not exist until its panel opens).
    this.tocService.scrollToSection$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(fieldKey => {
        if (!fieldKey) {
          return;
        }

        const tryScroll = (attempts: number) => {
          if (this.tocService.getScrollTarget() !== fieldKey) {
            return; // Cancel if a new scroll was initiated
          }

          // getElementById is global, so this finds the field wherever its lazy section
          // finally renders; poll until it appears (page switch + API + render delay).
          const wrapper = document.getElementById(fieldKey);
          if (wrapper) {
            wrapper.scrollIntoView({ behavior: 'auto', block: 'center' });
            // Apply highlight to the fillable field (mat-form-field), not the whole section.
            //
            // Only when the wrapper HOLDS one field, though. A group wrapper holds several, and
            // highlighting the first of them points at a line the caller never asked for — a deep link
            // whose caption matched a section heading would land on that section but flag its opening
            // field as the answer. Highlighting the group says what is actually true: the caption named
            // this section, and the field is one of the ones inside it.
            const fillableFields = wrapper.querySelectorAll('mat-form-field, mat-mdc-form-field');
            const fillableField = fillableFields.length === 1
              ? (fillableFields[0] as HTMLElement)
              : null;
            const highlightTarget = fillableField ?? wrapper;
            highlightTarget.classList.add('highlight-scroll');
            setTimeout(() => highlightTarget.classList.remove('highlight-scroll'), 2000);

            // Clear once successful so we don't accidentally scroll during re-renders
            this.tocService.scrollTo(null);
          } else if (attempts < 50) {
            setTimeout(() => tryScroll(attempts + 1), 100);
          }
        };

        tryScroll(0);
      });
  }

  validateForm() {
    this.validationService.validateAllForms();
  }


  private getPlatformDialogsByFlowchartId() {
    this.formResponseViewService.getPlatformDialogsByFlowchartId(
      this.eliteFormId,
      this.itemId,
      null,
      null,
      null,
      this.currentPlatformLink.fieldValues,
      this.currentPlatformLink.fieldValues['IsTranslated']
    ).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
      this.pages = response.fieldValues['EliteFormPages'] || this.pages;
      this.platformDialogsByPage = response.fieldValues['PlatformDialogs'];
      this.updateDeleteButtonState();
      this.applyFieldDeepLink();
    });
  }

  /**
   * FM-E-170-10: the caller asked for one field, not just this form — scroll to it.
   *
   * Driven from here rather than from the tool component for two reasons. Switching page indexes into
   * `this.pages`, so the pages must already be loaded; and this component owns the `scrollToSection$`
   * subscription, so by the time this runs the receiver exists. That second point is load-bearing:
   * `scrollTo` is backed by a BehaviorSubject and survives being called before anyone subscribes, but
   * `goToPage` is a plain Subject — a page switch requested too early is dropped in silence, which
   * would leave the scroll hunting on the wrong page for any target that isn't on page one.
   *
   * The server sends only the field KEY. It cannot send the element id: the page stamps
   * `fieldKey--dataItemId` and the data-item half exists only once the response holds a row, so on a
   * freshly minted Library preview every field inside a child-form or multi-response group has no data
   * item to name yet. This component does hold the rows and the paging, so it resolves both here.
   */
  private applyFieldDeepLink(): void {
    const fieldKey = this.currentPlatformLink?.fieldValues?.['DeepLinkFieldKey'];
    if (!fieldKey) {
      return;
    }
    if (this.isFieldDeepLinkApplied) {
      return;
    }
    this.isFieldDeepLinkApplied = true;

    // Deferred a tick for the same reason every page switch in this component is: the page index has
    // to land after the current render pass, not inside it.
    setTimeout(() => {
      const pageIndex = this.findPageIndexForField(fieldKey);
      if (pageIndex >= 0) {
        this.tocService.goToPage(pageIndex);
      }

      this.waitForFieldElement(fieldKey, 0);
    }, 0);
  }

  /**
   * Which page holds the field — read off the page model this component renders from, so the index is
   * one `goToPage` can use by construction.
   */
  private findPageIndexForField(fieldKey: string): number {
    const holds = (items: any[]): boolean =>
      (items || []).some(item => item?.metaData?.FieldKey === fieldKey || holds(item?.questions));

    return (this.pages || []).findIndex(page => holds(page?.questions));
  }

  /**
   * Poll until the field's wrapper exists, then hand its real id to the scroll seam.
   *
   * Matching on the `fieldKey--` prefix rather than a known id is the point: the suffix is the data
   * item, which this side learns only when the element renders. The wait is needed regardless — the
   * target sits behind `matExpansionPanelContent`, so it does not exist until its panel opens, and a
   * page switch has to complete first.
   */
  private waitForFieldElement(fieldKey: string, attempts: number, landedOnAncestor = false): void {
    const element = document.querySelector(`[id^="${fieldKey}--"]`) as HTMLElement;
    if (element) {
      this.focusDeepLinkTarget(element.id);
      return;
    }

    // A field inside a collapsed panel does not exist in the DOM at all — Material only mounts
    // `matExpansionPanelContent` when the panel opens — so no amount of waiting will find it. Open the
    // panels that own it, repeatedly: panels NEST, and an inner one is not in the DOM to be opened until
    // its parent has opened first, so one pass only ever gets through the outermost layer.
    if (attempts % 4 === 1) {
      this.expandAncestorPanels(fieldKey);
    }

    // Every tick, because this one has to observe a group over TIME before it may write to it.
    this.addMissingSections(fieldKey);

    // Provisionally land on the closest section that IS on screen, and then KEEP LOOKING. Some fields
    // never render — a multi-response group with no rows has no instance to scroll to — and landing on
    // the owning section beats appearing to do nothing. But this must not end the search: nested panels
    // can take several rounds to open, and settling here permanently was why a field under two collapsed
    // parents worked or failed depending on how fast the machine rendered.
    if (!landedOnAncestor && attempts >= 25) {
      for (const ancestor of this.fieldAncestry(fieldKey).slice(1)) {
        const fallback = document.querySelector(`[id^="${ancestor.key}--"]`) as HTMLElement;
        if (fallback) {
          this.focusDeepLinkTarget(fallback.id);
          landedOnAncestor = true;
          break;
        }
      }
    }

    // Generous, because reaching the field can now require server work: each repeating section that has
    // to be created is a command round-trip plus a re-render, and a field two levels deep needs two.
    if (attempts < 200) {
      setTimeout(() => this.waitForFieldElement(fieldKey, attempts + 1, landedOnAncestor), 100);
    }
  }

  /**
   * Point the scroll seam at an anchor, superseding whatever it was holding before.
   *
   * The generation token matters: a provisional landing on a section starts its own hold-in-view loop,
   * and when the real field turns up a moment later two loops would fight over the scroll position.
   */
  private focusDeepLinkTarget(anchorId: string): void {
    if (this.deepLinkAnchorId === anchorId) {
      return;
    }

    this.deepLinkAnchorId = anchorId;
    this.tocService.selectField(anchorId);
    this.tocService.scrollTo(anchorId);
    this.keepFieldInView(anchorId);
  }

  /**
   * The field and its ancestors, nearest first — the order to try when looking for something to open
   * or scroll to. Carries each one's caption because that is how a panel is identified on screen.
   */
  private fieldAncestry(fieldKey: string): { key: string; caption: string }[] {
    let chain: { key: string; caption: string }[] = [];

    const walk = (items: any[], ancestors: { key: string; caption: string }[]) => {
      (items || []).forEach(item => {
        const key = item?.metaData?.FieldKey;
        const caption = (item?.metaData?.Header || item?.metaData?.Label || '').trim();
        const trail = key ? [{ key, caption }, ...ancestors] : ancestors;
        if (key === fieldKey) {
          chain = trail;
        }

        walk(item?.questions, trail);
      });
    };

    (this.pages || []).forEach(page => walk(page?.questions, []));

    return chain;
  }

  /**
   * Create the repeating sections the field needs in order to exist, outermost first.
   *
   * A multi-response group is a repeating table: with no rows it renders an "Add Section" button and
   * nothing else, so a field inside it has no instance and cannot be scrolled to at all. Row 25 of the
   * live catalogue points at a field two such groups deep, both empty — the deep link had nothing to
   * aim at. Adding the row makes the field exist.
   *
   * One click per pass, and only at the deepest level that has actually rendered: an inner group is not
   * in the DOM to be filled until its parent holds a row, so the levels have to be created from the root
   * inwards, one render cycle at a time. The caller's poll supplies those cycles.
   *
   * NOTE: this WRITES to the response — it is creating rows the user did not ask for by hand. Harmless
   * on a Library preview, which is minted fresh per open; on a real form it means a deep link can leave
   * empty rows behind.
   */
  private addMissingSections(fieldKey: string): void {
    const inward = this.fieldAncestry(fieldKey).slice().reverse();
    if (inward.length < 2) {
      return;
    }

    for (let level = 0; level < inward.length - 1; level += 1) {
      const container = document.querySelector(`[id^="${inward[level].key}--"]`) as HTMLElement;
      if (!container) {
        return; // this level has not rendered yet — nothing to do until it does
      }

      // Ask the group how many rows it HAS, rather than whether the level inside it is on screen. Those
      // are different questions: a row that exists but sits collapsed renders none of its contents, so
      // reading the DOM depth counts a populated group as empty and adds a duplicate row to a response
      // that already had one.
      const key = inward[level].key;
      if (this.groupRowCount(container) > 0) {
        this.emptyObservations.delete(key);
        continue; // already has rows; the panel expansion will reveal what is inside them
      }

      // It LOOKS empty — but a group whose rows are still being fetched looks exactly the same, because
      // the editor renders nothing until its dialogs arrive. Writing on the first empty reading is what
      // put a duplicate row on responses that already had one. So require the emptiness to hold for a
      // sustained stretch, well past the render, before creating anything.
      const emptyFor = (this.emptyObservations.get(key) ?? 0) + 1;
      this.emptyObservations.set(key, emptyFor);
      if (emptyFor < EliteFormResponseContainerComponent.EmptyGroupConfirmTicks) {
        return;
      }

      // Guarded so a group is only ever asked once — the poll runs many times, and a click per tick
      // would pile up rows while the first was still being saved.
      if (this.sectionsRequested.has(key)) {
        return;
      }

      const addButton = Array.from(container.querySelectorAll('button.action-button'))
        .find(button => /add section/i.test((button as HTMLElement).innerText || '')) as HTMLElement;

      if (addButton) {
        this.sectionsRequested.add(key);
        addButton.click();
      }

      return;
    }
  }

  /**
   * Open the collapsed panels that own the field.
   *
   * Matched by caption rather than by key: a panel header renders the owning group's caption, and it
   * carries no field key of its own to match on. Only panels on the ancestor path are touched — opening
   * every collapsed panel would reorganise a form the caller never asked to change.
   */
  private expandAncestorPanels(fieldKey: string): void {
    const ancestry = this.fieldAncestry(fieldKey);
    if (!ancestry.length) {
      return;
    }

    const captions = ancestry
      .slice(1)
      .map(ancestor => ancestor.caption)
      .filter(caption => caption.length > 0);

    // Pass 1 — by caption, which is precise where a group has one.
    Array.from(document.querySelectorAll('mat-expansion-panel:not(.mat-expanded)')).forEach(panel => {
      const header = panel.querySelector('mat-expansion-panel-header') as HTMLElement;
      const text = (header?.innerText || '').trim();
      if (text && captions.some(caption => text.includes(caption))) {
        this.openPanel(header);
      }
    });

    // Pass 2 — by containment, because captions often are not there to match. A repeating group's rows
    // are themselves collapsed panels, and their header shows the PAGE title, not the group's caption;
    // the groups on that path can carry no caption at all. So descend to the deepest ancestor that has
    // rendered and open whatever is still shut inside it — each opening reveals the next level down.
    const inward = ancestry.slice().reverse();
    let deepest: HTMLElement | null = null;
    for (const ancestor of inward) {
      const element = document.querySelector(`[id^="${ancestor.key}--"]`) as HTMLElement;
      if (!element) {
        break;
      }

      deepest = element;
    }

    if (!deepest) {
      return;
    }

    // Capped: one level of a repeating group can hold many rows, and opening all of them at once turns
    // a scroll into a redraw of the whole page.
    Array.from(deepest.querySelectorAll('mat-expansion-panel:not(.mat-expanded)'))
      .slice(0, 4)
      .forEach(panel => this.openPanel(panel.querySelector('mat-expansion-panel-header') as HTMLElement));
  }

  /**
   * How many rows a repeating group currently holds.
   *
   * The group editor renders one `app-elite-form-group-editor-container` per row inside its own
   * `.group-container`, alongside the Add Section button — so counting those children is the only
   * honest read of "is this populated", and it stays correct whether the rows are open or collapsed.
   * Scoped to direct children so a nested group's rows are not counted as this one's.
   */
  private groupRowCount(container: HTMLElement): number {
    const groupContainer = container.querySelector('.group-container');
    if (!groupContainer) {
      return 0;
    }

    return groupContainer.querySelectorAll(':scope > app-elite-form-group-editor-container').length;
  }

  /** Open a panel once. Clicking toggles, so a second click would undo the first. */
  private openPanel(header: HTMLElement): void {
    if (!header || this.panelsOpened.has(header)) {
      return;
    }

    this.panelsOpened.add(header);
    header.click();
  }

  /**
   * Hold the field in view while the page finishes rendering.
   *
   * A page arrives progressively and IN BURSTS. Measured on the Finland QDMTT form: the target existed
   * once 8 of the page's 43 fields had rendered, so the first scroll is correct — and then every field
   * that renders above it pushes it down. It drifted 442px → 2843px, i.e. clean off screen, which is
   * why the deep link looked like it had done nothing.
   *
   * Re-scrolling once "when settled" does not work either: rendering paused for ~3s mid-way, any
   * stability window short enough to feel responsive reads that pause as finished, and the real burst
   * lands afterwards. So instead of guessing when the page is done, this simply corrects the drift
   * whenever it happens, for as long as the page keeps moving.
   *
   * The highlight is re-issued once the position has genuinely held, and re-armed if it moves again, so
   * what the user is left looking at is the field where it finally rests.
   */
  private keepFieldInView(anchorId: string): void {
    const deadline = performance.now() + 12000;
    let lastTop = Number.NaN;
    let stableTicks = 0;
    let highlighted = false;

    const tick = () => {
      // A later, better target took over (a provisional section giving way to the real field).
      if (this.deepLinkAnchorId !== anchorId) {
        return;
      }

      const element = document.getElementById(anchorId);
      if (!element) {
        return;
      }

      const top = Math.round(element.getBoundingClientRect().top);
      if (Math.abs(top - lastTop) > 2) {
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
        lastTop = Math.round(element.getBoundingClientRect().top);
        stableTicks = 0;
        highlighted = false;
      } else if (++stableTicks === 10 && !highlighted) {
        // ~1.5s without movement. Going back through the TOC seam re-runs the highlight, so the flash
        // marks the resting position rather than a spot the page has since scrolled away from.
        highlighted = true;
        this.tocService.scrollTo(anchorId);
      }

      if (performance.now() < deadline) {
        setTimeout(tick, 150);
      }
    };

    setTimeout(tick, 150);
  }

  // FM-E-55: a per-section Include succeeded (Reload:true). Re-fetch the whole page through the
  // existing seam so the now-editable section's fields arrive with validators restored — never a
  // local isDisabled flip in the section component.
  onSectionIncluded() {
    this.getPlatformDialogsByFlowchartId();
  }

  onFieldValueChange(event) {
    const formInfo = {
      ...event,
      formValue: this.inputForm.value,
      isValid: this.inputForm.valid
    }
    this.formValueChange.emit(formInfo);
  }

  goToNextPage() {
    const nextPageIndex = this.formResponseViewService.currentPageIndex + 1;
    this.formResponseViewService.currentEliteFormPage = this.pages[nextPageIndex];
    if (nextPageIndex < this.pages.length) {
      this.formResponseViewService.currentPageIndex = -1;
      setTimeout(() => {
        this.formResponseViewService.currentPageIndex = nextPageIndex;
        this.updateDeleteButtonState();
        this.tocService.setCurrentPage(nextPageIndex);
      }, 0);
    }
  }

  goToPreviousPage() {
    const previousPageIndex = this.formResponseViewService.currentPageIndex - 1;
    this.formResponseViewService.currentEliteFormPage = this.pages[previousPageIndex];
    if (previousPageIndex >= 0) {
      this.formResponseViewService.currentPageIndex = -1;
      setTimeout(() => {
        this.formResponseViewService.currentPageIndex = previousPageIndex;
        this.updateDeleteButtonState();
        this.tocService.setCurrentPage(previousPageIndex);
      }, 0);
    }
  }

  goToPage(index: number) {
    this.formResponseViewService.currentEliteFormPage = this.pages[index];
    if (index >= 0) {
      this.formResponseViewService.currentPageIndex = -1;
      setTimeout(() => {
        this.formResponseViewService.currentPageIndex = index;
        this.updateDeleteButtonState();
        this.onTogglePagination();
        this.tocService.setCurrentPage(index);
      }, 0);
    }
  }

  onTogglePagination() {
    this.showPageOptions = !this.showPageOptions;
  }


  trackById(index: number, page: EliteFormPageModel): string {
    return page.id;
  }

  /**
   * Get the total count of multi-response pages for the same group as the current page
   */
  getMultiResponsePageCount(currentPageIndex: number): number {
    if (!this.pages || currentPageIndex < 0 || currentPageIndex >= this.pages.length) {
      return 0;
    }

    const currentPage = this.pages[currentPageIndex];
    const currentPageId = currentPage.id;
    const currentFlowchartSectionId = currentPage.flowchartSectionId;

    return this.pages.filter(page =>
      page.id === currentPageId &&
      page.flowchartSectionId === currentFlowchartSectionId
    ).length;
  }


  isDeleteDisabled(currentPageIndex: number): boolean {
    const multiResponsePageCount = this.getMultiResponsePageCount(currentPageIndex);
    return multiResponsePageCount <= 1;
  }

  private updateDeleteButtonState(): void {
    this.isDeleteButtonDisabled = this.isDeleteDisabled(this.formResponseViewService.currentPageIndex);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    if (this.tocSubscription) {
      this.tocSubscription.unsubscribe();
    }
  }

  deleteMultipleResponsePage(currentPageIndex: number) {
    const command = new PlatformCommand('DeleteMultiResponsePageCommand', Api.FilingManagerApi);
    command.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;
    command.fieldValues['ResponseId'] = this.eliteFormFlowChartDataService.responseId;
    command.fieldValues['ParentItemId'] = this.itemId;
    command.fieldValues['FlowchartSectionId']=this.formResponseViewService?.currentEliteFormPage?.flowchartSectionId;
    command.fieldValues['MultiResponsePageCurrentIndex']=this.formResponseViewService?.currentEliteFormPage?.fieldValues['MultiResponsePageCurrentIndex'];
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(response => {
      const deletedPageId = this.pages[currentPageIndex].id;
      const deletedPageFlowchartSectionId = this.pages[currentPageIndex].flowchartSectionId;

      // Remove the page first
      this.pages.splice(currentPageIndex, 1);

      // First, reset IsLastPage flag for all pages in the same group
      for(let i = 0; i < this.pages.length; i++){
        if(this.pages[i].id === deletedPageId && this.pages[i].flowchartSectionId === deletedPageFlowchartSectionId){
          this.pages[i].fieldValues['IsLastPage'] = false;
        }
      }

      // Update subsequent pages with the same ID (sub-pages of the same group)
      let lastSubPageIndex = -1;
      for(let i = currentPageIndex; i < this.pages.length; i++){
        if(this.pages[i].id === deletedPageId && this.pages[i].flowchartSectionId === deletedPageFlowchartSectionId){
          // Update MultiResponsePageCurrentIndex for remaining sub-pages
          const activePageIndex = this.pages[i].fieldValues['MultiResponsePageCurrentIndex'] - 1;
          this.pages[i].fieldValues['MultiResponsePageCurrentIndex'] = activePageIndex;
          this.pages[i].title = `${this.pages[i].fieldValues['OriginalTitle'] ?? this.pages[i].title.split(' (')[0]} (${this.pages[i].fieldValues['GlobalIndex']}.${activePageIndex})`;
          lastSubPageIndex = i;
        }
      }

      // Find and set IsLastPage flag on the actual last sub-page of the group
      let actualLastPageIndex = -1;
      for(let i = this.pages.length - 1; i >= 0; i--){
        if(this.pages[i].id === deletedPageId && this.pages[i].flowchartSectionId === deletedPageFlowchartSectionId){
          actualLastPageIndex = i;
          break;
        }
      }

      if (actualLastPageIndex >= 0) {
        this.pages[actualLastPageIndex].fieldValues['IsLastPage'] = true;
      }

      // Navigate to appropriate page after deletion
      const newPageIndex = Math.min(currentPageIndex, this.pages.length - 1);
      this.formResponseViewService.currentPageIndex = newPageIndex;
      this.formResponseViewService.currentEliteFormPage = JSON.parse(JSON.stringify(this.pages[this.formResponseViewService.currentPageIndex]));
      this.updateDeleteButtonState();
      this.goToPage(newPageIndex);
      this.formResponseViewService.updateTableOfContents();
    });
  }
  addMultipleResponsePage(command: PlatformCommand, currentPageIndex: number) {
    command.fieldValues['IsClientSchema'] = this.eliteFormFlowChartDataService.isClientSchema;
    command.fieldValues['ResponseId'] = this.eliteFormFlowChartDataService.responseId;
    command.fieldValues['ParentItemId'] = this.itemId;
    command.fieldValues['RecordCountPerPage'] = this.formResponseViewService?.currentEliteFormPage?.fieldValues['RecordCountPerPage'];
    this.updateCurrentPageInfo(currentPageIndex);
    this.commandService.executePlatformCommand(command).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(response => {
      this.goToPage(currentPageIndex + 1);
      this.formResponseViewService.updateTableOfContents();
    });
  }

  private updateCurrentPageInfo(currentPageIndex: number) {
    const currentPage = JSON.parse(JSON.stringify(this.formResponseViewService.currentEliteFormPage));
    const existingPage = this.pages[currentPageIndex];
    existingPage.fieldValues['IsLastPage'] = false;
    const activePageIndex = Number(currentPage.fieldValues['MultiResponsePageCurrentIndex']) + 1;
    currentPage.fieldValues['MultiResponsePageCurrentIndex'] = activePageIndex;
    currentPage.title = `${currentPage.fieldValues['OriginalTitle'] ?? currentPage.title} (${currentPage.fieldValues['GlobalIndex']}.${activePageIndex})`;
    this.formResponseViewService.currentEliteFormPage = currentPage;
    this.pages.splice(currentPageIndex + 1, 0, currentPage);
  }
}
